import { POSTINGS_V3_GET_DATA } from '@revelio/data-access';

import { useMonthApiFilters } from '../../../../entity-summary/use-month-api-filters';
import { useFilterContext } from '../../../filters/contexts/FilterProvider';
import { useEffectiveFilters } from '../../../filters/hooks/useEffectiveFilters';
import type { PrimaryFilterId } from '../../../filters/types/filters';
import { hasPrimaryFilter } from '../../../filters/utils/common';
import { getPostingsV3Variables } from '../../../filters/utils/getPostingsV3Variables';
import { DataProviderQueryStatus } from '../types';
import { useUrqlQueryWithStatus } from '../utils/data-provider-results-utils';
import { useWeekRefs } from '../utils/useDateRefs';

export function usePostingsData(primaryDimension: PrimaryFilterId) {
  const { activeFilterMap } = useFilterContext();

  const postingEffectiveFilters = useEffectiveFilters({
    viewGranularity: 'week',
  });
  const compositionEffectiveFilters = useEffectiveFilters({
    viewGranularity: 'month',
  });
  const dateRangeApiFilters = useMonthApiFilters();

  const isPrimaryFilterSet = hasPrimaryFilter({
    primaryDimension,
    filters: compositionEffectiveFilters,
  });

  const postingsPaused = !isPrimaryFilterSet || !dateRangeApiFilters;

  const postingsV3Variables = getPostingsV3Variables({
    primaryDimension,
    effectiveFilters: postingEffectiveFilters,
    defaultMetricMode: 'expected_hires',
    activeFilterMap,
  });

  const {
    weekRefs,
    isLoading: isWeekRefsLoading,
    error: weekRefsError,
  } = useWeekRefs();

  const [{ data, status: postingsStatusV3 }] = useUrqlQueryWithStatus({
    query: POSTINGS_V3_GET_DATA,
    variables: postingsV3Variables,
    pause: postingsPaused || isWeekRefsLoading,
  });

  const status = weekRefsError
    ? DataProviderQueryStatus.Error
    : postingsStatusV3;

  return {
    postingsData: data?.postings_v3,
    weekRefs,
    status,
  };
}
